import Language, { SUPPORTED_LANGUAGES } from "@bimproject/language";
import { Translation } from "@bimproject/t9n";
import { useCallback, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";

import LanguageLabel from "./LanguageLabel";
import LanguageSelect from "./LanguageSelect";
import Button from "../../actions/Button";
import ListInput from "../ListInput";

export default function TranslatedInput(props: TranslatedInputProps) {
    const { disabled, onChange, suggestedLanguages = [], value = [] } = props;

    const languages = useMemo(
        () => SUPPORTED_LANGUAGES
            .filter(
                (language) => !value
                    .map(
                        (localization) => localization.language
                    )
                    .includes(language)
            )
            .sort(Language.compare)
            .join(";"),
        [value],
    );

    const Add = useCallback(
        (props) => (
            <AddLocalization
                disabled={ disabled }
                languages={ languages.split(";") }
                suggestedLanguages={ suggestedLanguages }
                { ...props }
            />
        ),
        [disabled, languages, suggestedLanguages],
    );

    return (
        <Styled
            Add={ Add }
            Input={ Inp }
            disabled={ disabled }
            itemKey={ (translation) => translation.language }
            label="translation"
            onChange={ onChange }
            value={
                value
                    .slice()
                    .sort(
                        (a, b) => Language.compare(a.language, b.language)
                    )
            }
        />
    );
}

interface TranslatedInputProps {
    disabled?: boolean
    label: string
    languages?: string[]
    onChange: (localized: Translation[]) => void
    suggestedLanguages?: string[]
    value: Translation[]
}

const Styled = styled(ListInput)`
    ol {
        column-gap: 8px;
        grid-template-columns: min-content 1fr min-content !important;
    }

    [data-language="en"] > div > span:nth-child(2) {
        display: none;
    }
`;

function Inp(props) {
    const { disabled, label, onChange, value } = props;
    return (
        <StyledTranslationInput>
            <LanguageLabel language={ value.language } />
            <input
                disabled={ disabled }
                onChange={ (event) => onChange({ language: value.language, text: event.target.value }) }
                placeholder={ `${ label } in ${ Language.fromCode(value.language).name }` }
                type="text"
                value={ value.text }
            />
        </StyledTranslationInput>
    );
}

const StyledTranslationInput = styled.div`
    display: grid;
    grid-column: 1 / -2;
    grid-template-columns: subgrid;
    padding: 0 4px;

    input {
        background: transparent;
        border: none;
        flex: auto;
        padding: 8px 8px;

        &::placeholder {
            color: #0003;
        }
    }
`;

function AddLocalization(props) {
    const { disabled, languages, onAdd, suggestedLanguages } = props;

    const buttonRef = useRef<HTMLButtonElement>();
    const dialogRef = useRef<HTMLDialogElement>();

    useEffect(
        () => {
            function onClick(event) {
                if (dialogRef.current.open && !dialogRef.current.contains(event.target) && event.target !== buttonRef.current) {
                    dialogRef.current.close();
                }
            }

            document.addEventListener("click", onClick, true);
            return () => document.removeEventListener("click", onClick, true);
        },
        [],
    );

    function toggle() {
        if (!dialogRef.current) return;
        if (dialogRef.current.open) {
            dialogRef.current.close();
        } else {
            dialogRef.current.show();
        }
    }

    if (!languages.length) return undefined;

    return (
        <StyledAddLocalization>
            <Button
                disabled={ disabled }
                onClick={ toggle }
                ref={ buttonRef }
            >Add translation</Button>
            <dialog ref={ dialogRef }>
                <LanguageSelect
                    languages={ SUPPORTED_LANGUAGES }
                    languagesDisabled={ SUPPORTED_LANGUAGES.filter((language) => !languages.includes(language)) }
                    languagesHighlighted={ suggestedLanguages }
                    onSelect={ (language) => onAdd({ language, text: "" }) }
                />
            </dialog>
        </StyledAddLocalization>
    );
}

const StyledAddLocalization = styled.div`
    grid-column: 1 / -1;
    position: relative;

    &:first-child > button {
        margin-top: 6px;
    }

    dialog {
        background: #fff;
        border: 1px solid #0007;
        border-radius: 4px;
        box-sizing: border-box;
        margin: 0;
        max-width: calc(4 * 176px + 3 * 8px + 2 * 16px + 2 * 1px + 1px); // 3 languages, 2 gaps, 2 paddings, 2 borders
        padding: 8px;
        position: absolute;
        top: calc(100% + 2px);
        width: 100%;
        z-index: 1;
    }
`;
